import BigNumber from 'bignumber.js';
import { Maybe } from '@paradigm/utils/types';
import { isValidDecimal, unformatDecimal } from '@paradigm/utils/formatDecimal';
import { Greeks } from '../../entities/greeks';
import { getProduct } from '../../../products/domain';

/**
 * Calculates the hedge leg quantity for an options trade.
 *
 * @param futureProductCode - The product code from the hedge/future leg.
 * @param optionProductCode - The product code for the option leg.
 * @param price - The price of the option leg.
 * @param greeks - The greeks for the option leg.
 * @returns {string | undefined} Returns undefined if any of delta, markPrice, price, futureProductCode,
 *                               or optionProductCode is undefined or invalid. Otherwise, returns the calculated quantity.
 *
 * Calculation begins with the delta value from premiumAdjustHedgeParams. The function then:
 * 1. Subtracts the mark price from delta if the base and quote currencies of the option are the same.
 * 2. Multiplies the resulting value by the option leg's price, applicable when the base and contract currencies of the
 *    future are different and during an RFQ auction.
 * 3. Rounds the final calculated value to the nearest minimum contract increment.
 */
export default function calculateHedgeLegQuantity(
  futureProductCode: Maybe<string>,
  optionProductCode: Maybe<string>,
  price: Maybe<string>,
  greeks: Greeks,
): Maybe<BigNumber> {
  const formattedDelta = greeks.delta;
  const formattedMarkPrice = greeks.mark_price;
  const formattedPrice = price;

  const haveDelta = isValidDecimal(formattedDelta);
  const haveMarkPrice =
    formattedMarkPrice != null && isValidDecimal(formattedMarkPrice);
  const haveLegPrice = formattedPrice != null && isValidDecimal(formattedPrice);
  const missingStrategyPrices = !haveDelta || !haveMarkPrice || !haveLegPrice;

  if (
    missingStrategyPrices ||
    futureProductCode == null ||
    optionProductCode == null
  )
    return undefined;

  const delta = unformatDecimal(formattedDelta);
  const markPrice = unformatDecimal(formattedMarkPrice);
  const parsedPrice = unformatDecimal(formattedPrice);

  const {
    currency: futureCurrency,
    contractCurrency: futureContractCurrency,
    minContractIncrement: futureMinContractIncrement,
    contractSize: futureContractSize,
  } = getProduct(futureProductCode);

  const { currency: optionCurrency, quoteCurrency: optionQuoteCurrency } =
    getProduct(optionProductCode);

  const minIncrement = BigNumber(futureMinContractIncrement).multipliedBy(
    futureContractSize,
  );

  const isOptionBaseAndQuoteCurrenciesEqual =
    optionCurrency === optionQuoteCurrency;

  const isFutureBaseAndContractCurrenciesEqual =
    futureCurrency === futureContractCurrency;

  const shouldSubtractMarkPrice = isOptionBaseAndQuoteCurrenciesEqual;
  const shouldMultiplyByLegPrice = !isFutureBaseAndContractCurrenciesEqual;

  const initialValue = BigNumber(delta);
  const subtractor = BigNumber(shouldSubtractMarkPrice ? markPrice : 0);
  const multiplier = BigNumber(shouldMultiplyByLegPrice ? parsedPrice : 1);
  const unroundedValue = initialValue
    .minus(subtractor)
    .multipliedBy(multiplier);

  const negativeOne = BigNumber(-1);
  const negativatedUnroundedValue = negativeOne.multipliedBy(unroundedValue);

  return roundToDivisor(negativatedUnroundedValue, minIncrement);
}

export function roundToDivisor(
  value: BigNumber,
  divisor: BigNumber,
): BigNumber {
  const safeDivisor = divisor.isZero() ? BigNumber(1) : divisor;
  return value
    .dividedBy(safeDivisor)
    .decimalPlaces(0, BigNumber.ROUND_UP)
    .multipliedBy(safeDivisor);
}
